import { useEffect, useMemo, useState } from "react";
import { useGetService } from "./useGetService";
import { API, EHotelServiceRequestType, EServiceRequestStatus, VendorTypeService } from "../constants";
import { getUserData, hotelServiceRequestTypeToLabelMap, serviceRequestTypeToLabelMap } from "../shared/utils";
import { EServiceRequestType } from "../types/global.types";
import dayjs from "dayjs";

const addPadding = (n: number) => String(n).padStart(2, "0");
const toDDMMYYYY = (d: Date) =>
  `${addPadding(d.getDate())}/${addPadding(d.getMonth() + 1)}/${d.getFullYear()}`;

export interface IInvoiceTableRow {
  id: string;
  tmId: string;
  arrivalDate: string;
  greeterId: string;
  greeterName: string;
  scannedByGreeterOn: string;
  requestType: EServiceRequestType | EHotelServiceRequestType;
  updatedOn: string;
  createdOn: string;
  status: string;
  serviceRequestId?: string;
}

export const useScannedDetails = (startDate: Date, endDate: Date) => {
  const [rows, setRows] = useState<IInvoiceTableRow[]>([]);

  const vendorId = getUserData()["cognito:username"];

  const url = useMemo(
    () =>
      `${API.CARNIVAL_ADMIN.GET_SERVICE_REQUEST_BY_VENDOR_ID}?vendorId=${vendorId}&status=${EServiceRequestStatus.ACCEPTED}&startDate=${toDDMMYYYY(startDate)}&endDate=${toDDMMYYYY(endDate)}`,
    [vendorId, startDate, endDate]
  );

  const { response, isLoading, refetch, error: serviceError } = useGetService(url);

  const isHotelVendor = getUserData()?.["custom:vendorType"] === VendorTypeService.HOTEL_ONLY;
  const hotelServiceRequestUrl = useMemo(
    () =>
      `${API.HOTEL.GET_HOTEL_SERVICE_REQUEST_BY_VENDOR_ID}?vendorId=${vendorId}&startDate=${toDDMMYYYY(startDate)}&endDate=${toDDMMYYYY(endDate)}`,
    [vendorId, startDate, endDate]
  );
  const {
    response: hotelServiceRequests,
    isLoading: isHotelServiceLoading,
    refetch: refetchHotelServiceRequests,
    error: hotelServiceError,
  } = useGetService(hotelServiceRequestUrl, !isHotelVendor);

  const {
    response: greeterResponse,
    isLoading: isGreeterLoading,
    error: greeterError,
  } = useGetService(`${API.HOTEL.GET_VENDOR_EMPLOYEES}`, !isHotelVendor);
  const greeterData = useMemo(() => {
    if (!greeterResponse?.data) return {};
    const data = greeterResponse?.data?.reduce((acc: any, row: any) => {
      acc[row.id] = row.name;
      return acc;
    }, {});

    return data || {};
  }, [greeterResponse]);

  useEffect(() => {
    if (isLoading || isHotelServiceLoading || isGreeterLoading) {
      setRows([]);
      return;
    }

    const tableRows: IInvoiceTableRow[] = [];

    if (response?.data?.length > 0) {
      response.data.forEach((row: any) => {
        tableRows.push({
          id: row.id,
          tmId: row.tmId || row.EmpId,
          arrivalDate: row.arrivalDate,
          greeterId: row.vendorEmpId,
          greeterName: row.vendorEmpName,
          scannedByGreeterOn: dayjs(row.scannedByGreeterOn || row.updatedOn).format("MM/DD/YYYY"),
          requestType: serviceRequestTypeToLabelMap[row.requestType as EServiceRequestType] || row.requestType,
          updatedOn: row.updatedOn,
          createdOn: row.createdOn,
          status: row.status,
        });
      });
    }

    if (hotelServiceRequests?.length > 0) {
      hotelServiceRequests.forEach((row: any) => {
        const [day, month, year] = (row.date || "")?.split("/") || [];
        const scannedByGreeterOn = row.date ? `${month}/${day}/${year}` : row.updatedOn ? dayjs(row.updatedOn).format("MM/DD/YYYY") : "";
        tableRows.push({
          id: row.id,
          tmId: row.tmId,
          serviceRequestId: row.serviceRequestId,
          arrivalDate: "",
          greeterId: row.vendorEmpId,
          greeterName: greeterData[row.vendorEmpId] || "",
          scannedByGreeterOn,
          requestType: "Meal Service: " + hotelServiceRequestTypeToLabelMap[row.requestType as EHotelServiceRequestType] || row.requestType,
          updatedOn: row.updatedOn,
          createdOn: row.createdOn,
          status: EServiceRequestStatus.ACCEPTED, // meal requests are always accepted because they are only created on scan.
        });
      });
    }

    setRows(tableRows);

  }, [response, isLoading, isHotelVendor, hotelServiceRequests, isHotelServiceLoading, isGreeterLoading, greeterData]);

  const refetchData = () => {
    refetch(url)
    if (isHotelVendor) {
      refetchHotelServiceRequests(hotelServiceRequestUrl)
    }
  };

  return {
    rows,
    isRowsLoading: isLoading || isHotelServiceLoading || isGreeterLoading,
    refetchData,
    error: serviceError || hotelServiceError || greeterError,
  };
};
